<template>
  <div>
    <b-form >
      <b-row>
        <b-col sm="2">
          <b-form-group label="Subject" label-class="control-label workload-course-label" :class="{ 'invalid-group': $v.course.subjectCode.$error }">
            <b-form-input v-model="course.subjectCode" :class="{ 'is-invalid': $v.course.subjectCode.$error }"></b-form-input>
          </b-form-group>
        </b-col>

        <b-col sm="2">
          <b-form-group label="Number" label-class="control-label workload-course-label" :class="{ 'invalid-group': $v.course.catalogNumber.$error }">
            <b-form-input v-model="course.catalogNumber" :class="{ 'is-invalid': $v.course.catalogNumber.$error }"></b-form-input>
          </b-form-group>
        </b-col>

        <b-col sm="6">
          <b-form-group label="Course Title" label-class="control-label workload-course-label" :class="{ 'invalid-group': $v.course.courseTitle.$error }">
            <b-form-input v-model="course.courseTitle" :class="{ 'is-invalid': $v.course.courseTitle.$error }"></b-form-input>
          </b-form-group>
        </b-col>

        <b-col sm="2">
          <b-form-group label="Units" label-class="control-label workload-course-label">
            <b-form-input v-model="course.units" placeholder="optional" type="number"></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col sm="12">
          <b-form-group label="Notes" label-class="control-label workload-course-label">
            <b-form-input v-model="course.notes" placeholder="Optional"></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col sm="12">
          <b-form-checkbox v-model="course.contributesToTeaching" class="font-weight-bold">Contributes to Teaching %</b-form-checkbox>
          <font-awesome-icon icon="info-circle" v-b-popover.hover="'If this is selected, the course will count towards your Teaching requirements. Otherwise, it will still appear on your course list, but not contribute to the Teaching percentage.'" />
        </b-col>
      </b-row>

      <div v-if="courseSuggestions.length">
        <h6>Suggestions</h6>
        <b-form-select :options="courseSuggestionOptions" :select-size="5" @input="selectCourse"></b-form-select>
      </div>

      <span v-if="$v.course.$error && (!course.subjectCode || !course.catalogNumber || !course.courseTitle)" class="span-feedback invalid-feedback">
        All fields must be filled out.
      </span>
      <span v-if="course.subjectCode && course.subjectCode.length > subjectMaxLength" class="span-feedback invalid-feedback">
        Subject length cannot exceed {{subjectMaxLength}}.
      </span>
      <span v-if="course.catalogNumber && course.catalogNumber.length > numberMaxLength" class="span-feedback invalid-feedback">
        Number length cannot exceed {{numberMaxLength}}.
      </span>
      <span v-if="course.courseTitle && course.courseTitle.length > titleMaxLength" class="span-feedback invalid-feedback">
        Course title length cannot exceed {{titleMaxLength}}.
      </span>
    </b-form>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { required, maxLength } from "vuelidate/lib/validators";
import * as _ from "lodash"; // eslint-disable-line no-unused-vars

export default {
  data() {
    return {
      course: {
        subjectCode: null,
        catalogNumber: null,
        courseTitle: null,
        units: null,
        notes: null,
        contributesToTeaching: true,
        new: true
      },
      subjectMaxLength: 6,
      numberMaxLength: 18,
      titleMaxLength: 30
    };
  },

  props: {
    editingCourse: {
      type: Object,
      required: false
    },

    value: {
      type: Boolean,
      required: true
    }
  },

  computed: {
    ...mapState({
      courseSuggestions: state => state.courseForm.courseSuggestions
    }),

    courseSuggestionOptions: function() {
      return _.map(this.courseSuggestions, function(courseSuggestion) {
        return {
          text:
            courseSuggestion.subject +
            " " +
            courseSuggestion.catalogNumber +
            " - " +
            courseSuggestion.description,
          value: courseSuggestion
        };
      });
    }
  },

  watch: {
    value: function() {
      if (this.value) {
        this.$v.$touch();
        if (!this.$v.$error) {
          // set whether this course is new or not
          this.course.new = this.checkIfNewCourse();
          this.$emit("complete", this.course);
          this.$v.$reset();
          this.course = {
            subjectCode: null,
            catalogNumber: null,
            courseTitle: null,
            units: null,
            notes: null
          };
        } else {
          this.$emit("input", false);
        }
      }
    },

    editingCourse: function() {
      if (this.editingCourse) {
        this.setEditCourse();
      }
    },

    "course.subjectCode": function() {
      this.populateCourseSuggestions(this.course);
    },

    "course.catalogNumber": function() {
      this.populateCourseSuggestions(this.course);
    },

    "course.courseTitle": function() {
      this.populateCourseSuggestions(this.course);
    }
  },

  validations: {
    course: {
      subjectCode: { required, maxLength: maxLength(6) },
      catalogNumber: { required, maxLength: maxLength(18) },
      courseTitle: { required, maxLength: maxLength(30) }
    }
  },

  methods: {
    ...mapActions("courseForm", ["populateCourseSuggestions"]),

    setEditCourse: function() {
      this.course.subjectCode = this.editingCourse.subjectCode;
      this.course.catalogNumber = this.editingCourse.catalogNumber;
      this.course.courseTitle = this.editingCourse.courseTitle;
      this.course.units = this.editingCourse.units;
      this.course.notes = this.editingCourse.notes;
      this.course.contributesToTeaching = this.editingCourse.contributesToTeaching;
      this.course.new = this.editingCourse.new;
    },

    getCourseSuggestions: function() {
      this.populateCourseSuggestions().catch(function(error) {
        console.log(error);
      });
    },

    selectCourse(value) {
      this.course.subjectCode = value.subject;
      this.course.catalogNumber = value.catalogNumber;
      this.course.courseTitle = value.description;
      this.course.units = value.unitsMaximum;
      this.course.contributesToTeaching = value.contributesToTeaching;
    },

    // returns false if this matches a recommended course, true if it is a new course being added
    // method instead of computed property because we only need to calculate this once, not continuously
    checkIfNewCourse() {
      var that = this;
      var isNewCourse = true;
      _.forEach(this.courseSuggestions, function(courseSuggestion) {
        if (
          courseSuggestion.subject === that.course.subjectCode &&
          courseSuggestion.catalogNumber === that.course.catalogNumber &&
          courseSuggestion.description === that.course.courseTitle &&
          courseSuggestion.contributesToTeaching ===
            that.course.contributesToTeaching
        ) {
          isNewCourse = false;
        }
      });
      return isNewCourse;
    }
  },

  created() {
    if (this.editingCourse) {
      this.setEditCourse();
    }
    this.populateCourseSuggestions(this.course);
  }
};
</script>

<style scoped>
</style>
